import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadStatement } from "@/components/Statements";
import { MagnifyingGlass, Eraser, FilePdf } from "@phosphor-icons/react";

export default function AdminArtistTools({ onChanged }) {
  const [q, setQ] = useState("");
  const [artists, setArtists] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/artists", { params: { q } }).then(({ data }) => setArtists(data.artists)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reset = async (a) => {
    if (!window.confirm(`Reset ALL earnings and payouts for ${a.display_name} (${a.email}) to zero?\n\nThis deletes their stream reports and payout history. It cannot be undone.`)) return;
    setBusy(a.id);
    try {
      const { data } = await api.post(`/admin/artists/${a.id}/reset-earnings`);
      toast.success(`${data.artist}: ${data.reports_deleted} report rows + ${data.payouts_deleted} payouts cleared`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-zinc-800 mb-8" data-testid="admin-artist-tools">
      <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-950 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Artist tools · statements & test-data reset</div>
      <div className="p-6">
        <div className="relative max-w-md mb-4">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search artist by name or email" data-testid="artist-search"
            className="w-full bg-transparent border border-zinc-800 pl-9 pr-3 py-2 text-xs focus:border-red-600 focus:outline-none" />
        </div>
        <div className="divide-y divide-zinc-800 border border-zinc-800" data-testid="artist-list">
          {artists.length === 0 ? <div className="p-6 text-xs text-zinc-500">No artists match.</div> : artists.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs row-hover" data-testid={`artist-row-${a.id}`}>
              <div className="flex-1 min-w-[200px]"><div>{a.display_name} {a.role === "admin" && <span className="text-[9px] text-red-500 tracking-widest uppercase ml-1">admin</span>}</div><div className="text-[10px] text-zinc-500">{a.email}{a.subscription_tier && ` · ${a.subscription_tier}`}</div></div>
              <button onClick={() => downloadStatement(`/admin/artists/${a.id}/statements/all.pdf`, `statement-${a.email}.pdf`).catch(() => toast.error("Could not build statement"))} data-testid={`artist-statement-${a.id}`} className="border border-zinc-700 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-white inline-flex items-center gap-1"><FilePdf size={12} /> Statement</button>
              <button onClick={() => reset(a)} disabled={busy === a.id} data-testid={`artist-reset-${a.id}`} className="border border-zinc-700 text-zinc-400 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-red-600 hover:text-red-500 inline-flex items-center gap-1 disabled:opacity-40"><Eraser size={12} /> Reset earnings</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
